import * as React from 'react';
import _isEmpty from 'lodash/isEmpty';

import {
  BirthCertificateDocList,
  BirthCertificateDocs,
  DocAccessType,
  Education,
  EducationDocList,
  EducationSponsorship,
  IDocumentsList,
  IRisk,
} from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { formatCurrency, slug } from '../../../../utilities/formatting';
import LoanLetter from '../../../sections/financeDocs/loanLetter';
import BirthCertificate from '../../../sections/financeDocs/birthCertificate';
import { hasFundingRisks, isFundingRisk } from '../../../../utilities/risks';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { isLoanTypeUnknown, loanTypeLabel } from '../../../organisms/finance/EducationSponsor/utils';
import { UserRole } from '../../../../store/auth/types';
import { SummaryGroup } from '../../../Summary/SummaryGroup';
import { SummaryRow } from '../../../Summary/SummaryRow';
import { useTranslation } from "react-i18next";

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  id: string;
  documents: Education;
  birthCertificate?: BirthCertificateDocs;
  financials: EducationSponsorship;
  risk: IRisk['fundingRisk']['educationSponsorship'];
  docRisk: IRisk['financeDocsRisk'];
  title?: string;
  warning?: string;
  userRole?: UserRole;

  canCheckOff?: boolean;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  showRisks: boolean;

  isCheckedOffDocRisks?: (docType: string, docKey: string) => boolean;
  isCheckedOffFinanceRisks?: (docType: string, docKey: string) => boolean;
  onCheckOffDocRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onCheckOffFinanceRisks?: (docType: string, docKey: string, docTitle: string) => void;
  onResume: (url: string) => void;
  onUploadClick: (sponsorType: string, docType: string | null, docKey: string, metaData?: string) => void;
  onOpenClick: (sponsorType: string, docType: string | null, docKey: string) => void;
  onApproveClick: (sponsorType: string, docType: string | null, docKey: string) => void;
  onRejectClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onRescindRejectClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onDeleteClick: (sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => void;
  onAcknowledgeClick: (sponsorType: string, docType: string | null, docKey: string) => void;
}

const EducationSponsor = (props: IProps) => {
    const { t } = useTranslation('common');
  const { showRisks, infoOnly, userAccess, userRole } = props;
  const getMetaData = () => {
    return (
      <SummaryGroup key={'education'} title={props.financials?.loanDesc || undefined} boxProps={{ as: 'div', pt: 0 }}>
        <SummaryRow
          key={`education_loan_description_${props.id}`}
          label={t('common:loanType')}
          fullLabel="Which of the below best describes your loan?"
          value={loanTypeLabel(props?.financials?.loanType)}
          isRisk={showRisks && isFundingRisk(props.risk, 'loanType', props.id)}
        />

        {isLoanTypeUnknown(props.financials?.loanType) ? (
          <SummaryRow
            key={`education_loan_description_other_${props.id}`}
            label={t('common:loanDescription')}
            fullLabel="Please describe your loan below"
            value={props.financials.loanDesc || ''}
            isRisk={showRisks && true}
          />
        ) : null}

        <SummaryRow
          key={`education_loan_name_${props.id}`}
          label={t('common:loanName')}
          fullLabel="What is the name of the loan?"
          value={props.financials.loanName || ''}
        />

        <SummaryRow
          key={`education_loan_in_name_${props.id}`}
          label={t('common:loanName')}
          fullLabel="Whose name is on the loan?"
          value={props.financials.personDesc}
          isRisk={showRisks && isFundingRisk(props.risk, 'personDesc', props.id)}
        />

        {props.financials.personName === 'Other' ? (
          <SummaryRow
            key={`education_loan_relationship_${props.id}`}
            label={t('common:relationshipToPerson')}
            fullLabel="What is your relationship to the person?"
            value={props.financials.personDesc}
            isRisk={showRisks && true}
          />
        ) : null}

        {['Mother', 'Father', 'Guardian'].includes(props.financials.personName) ? (
          <>
            <SummaryRow
              key={`education_loan_guardian_first_name_${props.id}`}
              label={t('common:sFirstName', { personName: props.financials.personName })}
              value={props.financials.personFirstName}
              isRisk={showRisks && true}
            />

            <SummaryRow
              key={`education_loan_guardian_family_name_${props.id}`}
              label={t('common:sFamilyName', { personName: props.financials.personName })}
              value={props.financials.personSurname}
              isRisk={showRisks && true}
            />
          </>
        ) : null}

        {!isLoanTypeUnknown(props.financials?.loanType) ? (
          <>
            <SummaryRow
              key={`education_loan_country_${props.id}`}
              label={t('common:country')}
              value={props.financials.country!}
              isRisk={showRisks && isFundingRisk(props.risk, 'country', props.id)}
            />

            <SummaryRow
              key={`education_loan_bank_name_${props.id}`}
              label={t('common:loanProviderName')}
              value={props.financials.bankName!}
              isRisk={showRisks && isFundingRisk(props.risk, 'bankName', props.id)}
            />
          </>
        ) : null}

        <SummaryRow
          key={`education_loan_contribution_amount_${props.id}`}
          label={t('common:contributionAmount')}
          value={`£${formatCurrency(props.financials.sponsorshipAmount)}`}
          isRisk={showRisks && false}
        />
      </SummaryGroup>
    );
  };

  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];
  const { birthCertificate } = props;
  if (birthCertificate && birthCertificate.certificate) {
    const visibleDocs = getVisible({
      documentsHash: birthCertificate.certificate,
      userAccess,
      infoOnly,
      userRole,
    }) as BirthCertificateDocList[];
    if (hasDeleted(birthCertificate.certificate)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BirthCertificateDocList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`birth-certificate-${key}`}
              key={`${slug(props.id)}-birth-certificate-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:birthCertificateOrProofOfGuardia', { index: Object.keys(birthCertificate).length > 1 ? (index > 0 ? index + 1 : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('birthCertificate', key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks && props.onCheckOffDocRisks('birthCertificate', key, 'Birth Certificate')
              }
              onUploadClick={() => props.onUploadClick('birthCertificate', 'certificate', key)}
              onOpenClick={() => props.onOpenClick('birthCertificate', 'certificate', key)}
              onApproveClick={() => props.onApproveClick('birthCertificate', 'certificate', key)}
              onRejectClick={() => props.onRejectClick('birthCertificate', 'certificate', key, 'Birth Certificate')}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('birthCertificate', 'certificate', key, 'Birth Certificate')
              }
              onDeleteClick={() => props.onDeleteClick('birthCertificate', 'certificate', key, 'Birth Certificate')}
              onAcknowledgeClick={() => props.onAcknowledgeClick('birthCertificate', 'certificate', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {item.status !== 'AWAITING' && item.fileNames && item.fileNames.length > 0 ? (
                <>
                  <BirthCertificate
                    id={key}
                    showRisks={showRisks}
                    risk={props.docRisk.birthCertificate}
                    isLegalDocument={item.isLegalDocument!}
                    hasAllNames={item.hasAllNames!}
                    inEnglish={item.isInEnglish!}
                    hasTranslation={item.hasProvidedTranslation!}
                    uploadedBy={item.uploadedBy}
                  />
                </>
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }
  if (birthCertificate && birthCertificate.translation) {
    const visibleDocs = getVisible({
      documentsHash: birthCertificate.translation,
      userAccess,
      infoOnly,
      userRole,
    }) as IDocumentsList[];
    if (hasDeleted(birthCertificate.translation)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          return (
            <FingerWithApprove
              id={`birth-certificate-translation-${key}`}
              key={`${slug(props.id)}-birth-certificate-translation-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:birthCertificateTranslation', { index: Object.keys(birthCertificate).length > 1 ? (index > 0 ? index + 1 : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('birthCertificate', key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks &&
                props.onCheckOffDocRisks('birthCertificate', key, 'Birth Certificate Translation')
              }
              onUploadClick={() => props.onUploadClick('birthCertificate', 'translation', key)}
              onOpenClick={() => props.onOpenClick('birthCertificate', 'translation', key)}
              onApproveClick={() => props.onApproveClick('birthCertificate', 'translation', key)}
              onRejectClick={() =>
                props.onRejectClick('birthCertificate', 'translation', key, 'Birth Certificate Translation')
              }
              onRescindRejectClick={() =>
                props.onRescindRejectClick('birthCertificate', 'translation', key, 'Birth Certificate Translation')
              }
              onDeleteClick={() =>
                props.onDeleteClick('birthCertificate', 'translation', key, 'Birth Certificate Translation')
              }
              onAcknowledgeClick={() => props.onAcknowledgeClick('birthCertificate', 'translation', key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            />
          );
        }),
      );
    }
  }

  const educationSponsor = props.documents;
  if (educationSponsor && !_isEmpty(educationSponsor)) {
    const visibleDocs = getVisible({
      documentsHash: educationSponsor,
      userAccess,
      infoOnly,
      userRole,
    }) as EducationDocList[];
    if (hasDeleted(educationSponsor)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: EducationDocList, index: number) => {
          const { key, item } = doc;

          return (
            <FingerWithApprove
              id={`${key}-${props.id}`}
              key={`${slug(props.id)}-rejected-bank-statement-${key}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              isDocumentFixed={item.isDocumentFixed}
              reason={item.reason}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              label={t('common:text4', { loanName: props.financials.loanName, index: Object.keys(educationSponsor).length > 1 ? (index > 0 ? index + 1 : '') : '' })}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              canCheckOff={props.canCheckOff}
              isCheckedOff={() =>
                props.isCheckedOffDocRisks ? props.isCheckedOffDocRisks('educationSponsorship', props.id + key) : false
              }
              onCheckOffRisk={() =>
                props.onCheckOffDocRisks &&
                props.onCheckOffDocRisks(
                  'educationSponsorship',
                  props.id + key,
                  `${props.financials.loanName} (Education Sponsorship)`,
                )
              }
              onUploadClick={() => props.onUploadClick('educationSponsorship', null, key)}
              onOpenClick={() => props.onOpenClick('educationSponsorship', null, key)}
              onApproveClick={() => props.onApproveClick('educationSponsorship', null, key)}
              onRejectClick={() => props.onRejectClick('educationSponsorship', null, key, 'Education Sponsor')}
              onRescindRejectClick={() =>
                props.onRescindRejectClick('educationSponsorship', null, key, 'Education Sponsor')
              }
              onDeleteClick={() => props.onDeleteClick('educationSponsorship', null, key, 'Education Sponsor')}
              onAcknowledgeClick={() => props.onAcknowledgeClick('educationSponsorship', null, key)}
              buttonLabel={'How to prove'}
              resolveLabel={'Requires attention'}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {props.financials?.loanType &&
              !isLoanTypeUnknown(props.financials?.loanType) &&
              item.status !== 'AWAITING' &&
              item.fileNames &&
              item.fileNames.length > 0 ? (
                <LoanLetter
                  id={props.id + key}
                  showRisks={showRisks}
                  risk={props.docRisk.educationSponsorship}
                  hasOfficialLetterhead={item.hasOfficialLetterhead!}
                  nameAndLogoVisible={item.nameAndLogoVisible!}
                  officiallySigned={item.officiallySigned!}
                  hasDate={item.hasDate!}
                  dateOnLetter={item.dateOnLetter!}
                  selectedTextBodyReq={item.selectedTextBodyReq!}
                  hasContactDetails={item.hasContactDetails!}
                  fundConditions={item.fundConditions!}
                  isMoreThan6MonthsOld={item.isDateOlderThan6Months!}
                  uploadedBy={item.uploadedBy}
                />
              ) : undefined}
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id={`education-sponsorship-${props.id}`}
      label={t('common:educationLoan')}
      isRisk={showRisks && hasFundingRisks(props.risk, props.id)}
      isRiskSection={showRisks && hasFundingRisks(props.risk, props.id)}
      canCheckOff={props.canCheckOff}
      isCheckedOff={() =>
        props.isCheckedOffFinanceRisks ? props.isCheckedOffFinanceRisks('educationSponsorship', props.id) : false
      }
      onCheckOffRisk={() =>
        props.onCheckOffFinanceRisks &&
        props.onCheckOffFinanceRisks('educationSponsorship', props.id, 'Education Sponsorship')
      }
      canEdit={props.canResume}
      edit={() => {
        props.onResume('finances/step-1');
      }}
      metaData={getMetaData()}
      minMetaDataHeight={300}
      warning={props.warning}
      amount={`£${formatCurrency(props.financials.sponsorshipAmount)}`}
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default EducationSponsor;
